import { useEffect, useMemo, useState } from "react";

import toast from "components/partials/toast/toast";
import {
  cleanupFormDataForSave,
  getNestedValue,
  mergeTabFormData,
  setNestedValue,
} from "containers/admin/clients/client/components/form-builder/form-builder";
import { useSmsChannelValueOptions } from "containers/admin/clients/client/campaign-templates-page/use-sms-channel-value-options";
import { getFormSchemaSidebarItems } from "containers/admin/clients/client/components/form-builder/form-schema";
import type { FormPageTab } from "containers/admin/clients/client/components/form-page-layout/form-page-layout";
import {
  createStrategyFormTabs,
  getStrategyData,
  isDynamicFormSchema,
  type StrategyFieldOverrides,
} from "containers/admin/clients/client/programs-page/program-strategy-form.utils";
import {
  CAMPAIGN_OBJECTIVE_FIELD_KEYS,
  useCampaignObjectiveCascadeOverrides,
} from "containers/admin/clients/client/programs-page/use-campaign-objective-cascade-overrides";

import { useCampaignTemplateSchema } from "hooks/api/use-campaign-templates";
import { useCurrentCampaign, usePartiallyUpdateCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useTouchpoints } from "hooks/api/use-touchpoints";
import { useSyncedDraftData } from "hooks/use-synced-draft-data";
import { useTabDirtyState } from "hooks/use-tab-dirty-state";

const STRATEGY_FIELD_OVERRIDES: StrategyFieldOverrides = {
  "campaign-details": { campaignCdnProfile: { "x-disabled": false, description: undefined } },
};

export const useCampaignStrategyForm = () => {
  const campaign = useCurrentCampaign();
  const currentClient = useCurrentClient();
  const patchCampaignMutation = usePartiallyUpdateCampaign();
  const { refetch: refetchTouchpointsForObjectiveWarning } = useTouchpoints(
    {},
    { enabled: false },
  );
  const [isEditing, setIsEditing] = useState(false);
  const [tabRevisions, setTabRevisions] = useState<Record<string, number>>({});

  const serverSavedData = useMemo(
    () => getStrategyData(campaign.strategyData),
    [campaign.strategyData],
  );
  const [localSavedData, setLocalSavedData] = useState(serverSavedData);

  useEffect(() => {
    setLocalSavedData(serverSavedData);
  }, [campaign.id, serverSavedData]);

  const { data: schemaResponse, isLoading: isSchemaLoading } = useCampaignTemplateSchema({
    version: campaign.schemaVersion,
  });

  const schema = useMemo(
    () => (isDynamicFormSchema(schemaResponse) ? schemaResponse : undefined),
    [schemaResponse],
  );

  const { draftData, setDraftData, draftDataRef } = useSyncedDraftData({
    savedData: localSavedData,
    isEditing,
    resetKey: campaign.id,
  });

  const cascadeOverrides = useCampaignObjectiveCascadeOverrides({
    schemaVersion: campaign.schemaVersion,
    programType: campaign.programType,
    draftData,
    setDraftData,
    isEditing,
    baseOverrides: STRATEGY_FIELD_OVERRIDES,
  });
  const fieldOverrides = useSmsChannelValueOptions(draftData, cascadeOverrides);

  const tabs = useMemo(
    () => (schema ? createStrategyFormTabs(schema, fieldOverrides) : []),
    [schema, fieldOverrides],
  );
  const sidebarItems = useMemo(
    () => (schema ? getFormSchemaSidebarItems(schema) : undefined),
    [schema],
  );

  const { hasChanges } = useTabDirtyState({ tabs, draftData, savedData: localSavedData, isEditing });

  const getTabValues = (tab: FormPageTab) =>
    getStrategyData(getNestedValue(draftData, tab.dataPath ?? [tab.id]));

  const warnIfCampaignHasTouchpoints = () => {
    refetchTouchpointsForObjectiveWarning().then(({ data }) => {
      if ((data?.totalElements ?? 0) > 0) {
        toast.warning({
          data: {
            title: "Campaign objective changed",
            message:
              "Changing campaign objective may mismatch current creative content types. Please review and update them.",
          },
          autoClose: false,
        });
      }
    });
  };

  const handleTabValuesChange = (tab: FormPageTab, values: Record<string, unknown>) => {
    const dataPath = tab.dataPath ?? [tab.id];
    const previousTabValues = getTabValues(tab);

    const objectiveFieldChanged = CAMPAIGN_OBJECTIVE_FIELD_KEYS.some(
      (fieldKey) => fieldKey in values && values[fieldKey] !== previousTabValues[fieldKey],
    );

    if (objectiveFieldChanged) {
      warnIfCampaignHasTouchpoints();
    }

    setDraftData((prev) => setNestedValue(prev, dataPath, values));
  };

  const bumpTabRevisions = () => {
    setTabRevisions((prev) =>
      Object.fromEntries(tabs.map((tab) => [tab.id, (prev[tab.id] ?? 0) + 1])),
    );
  };

  const handleDiscard = () => {
    setDraftData(localSavedData);
    bumpTabRevisions();
  };

  const handleSubmit = (formData: Record<string, unknown>, tab: FormPageTab) => {
    const dataPath = tab.dataPath ?? [tab.id];
    const existingTabData = getStrategyData(getNestedValue(draftDataRef.current, dataPath));
    const mergedTabData = mergeTabFormData(existingTabData, formData);
    const tabValues = cleanupFormDataForSave(tab.schema, mergedTabData);
    const nextStrategyData = setNestedValue(draftDataRef.current, dataPath, tabValues);

    patchCampaignMutation.mutate(
      {
        clientId: currentClient.id,
        id: campaign.id,
        data: { strategyData: nextStrategyData },
      },
      {
        onSuccess: (updatedCampaign) => {
          setLocalSavedData(getStrategyData(updatedCampaign.strategyData));
          setIsEditing(false);
          toast.success({ data: { title: "Campaign strategy saved" } });
        },
        onError: () => {
          toast.error({
            data: {
              title: "Could not save campaign strategy",
              message: "Please try again.",
            },
          });
        },
      },
    );
  };

  const handleSaveTitle = (name: string) => {
    patchCampaignMutation.mutate(
      {
        clientId: currentClient.id,
        id: campaign.id,
        data: { name },
      },
      {
        onSuccess: () => {
          toast.success({ data: { title: "Campaign name saved" } });
        },
        onError: () => {
          toast.error({
            data: {
              title: "Could not save campaign name",
              message: "Please try again.",
            },
          });
        },
      },
    );
  };

  return {
    campaign,
    tabs,
    sidebarItems,
    isSchemaLoading,
    isEditing,
    setIsEditing,
    hasChanges,
    tabRevisions,
    getTabValues,
    handleTabValuesChange,
    handleDiscard,
    handleSubmit,
    handleSaveTitle,
    isSaving: patchCampaignMutation.isPending,
  };
};
